import supabaseClient from '@/api/supabaseClient.ts';
import { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TablesUpdate } from '@/_constants/database.types';

type UpdateSubjectsRequest = Pick<
  TablesUpdate<'users'>,
  'subjects' | 'exam_board' | 'predicted_grade' | 'aspirational_grade' | 'onboarding_completed'
>;

const useUpdateSubjects = () => {
  const client = supabaseClient;
  const queryClient = useQueryClient();

  const mutationFn = async (request: UpdateSubjectsRequest) => {
    return updateSubjects(client, request);
  };

  return useMutation({
    mutationFn,
    onSuccess: async () => {
      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.refetchQueries({ queryKey: ['user'] });
    },
  });
};

export default useUpdateSubjects;

async function updateSubjects(client: SupabaseClient, request: UpdateSubjectsRequest): Promise<void> {
  const {
    data: { user },
  } = await client.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const updateData: TablesUpdate<'users'> = {};

  if (request.subjects) {
    updateData.subjects = request.subjects;
  }

  if (request.exam_board) {
    updateData.exam_board = request.exam_board;
  }

  if (request.predicted_grade) {
    updateData.predicted_grade = request.predicted_grade;
  }

  if (request.aspirational_grade) {
    updateData.aspirational_grade = request.aspirational_grade;
    updateData.onboarding_completed = true;
  }

  const { error } = await client.from('users').update(updateData).eq('auth_id', user.id);

  if (error) {
    throw new Error(error.message);
  }
}
